/**
 * Alergénios e ingredientes prováveis de um produto na revisão do import
 * (#325, PR 2). Mesmos chips e estados do editor do produto: o dono pode
 * confirmar aqui (vale no commit) ou deixar "a confirmar" para depois.
 * Sem estado de servidor: as decisões vivem na ReviewPanel (por categoria +
 * nome) e vão no rascunho do commit.
 */
import { useState } from 'react'
import { isAllergenCode } from '@self-restaurant/shared'
import type { ImportDraftProduct } from '../lib/api.js'
import {
  allergenLabel,
  formatAllergenList,
  cycleIngredient,
  draftAllergenBadgeState,
  draftAllergenState,
  draftSelection,
  draftSourceOf,
  ingredientDecision,
  toggleCode,
  type DraftDecision,
} from '../lib/allergens.js'
import { AllergenBadge, AllergenChips, AllergenLegend } from './AllergenChips.js'
import { cn } from '../lib/utils.js'
import { useI18n } from '../lib/i18n.js'

export function ImportReviewAllergens({
  product,
  decision,
  onChange,
  testId,
}: {
  /** produto do rascunho com a decisão já aplicada (withDecision) */
  product: ImportDraftProduct
  decision: DraftDecision | undefined
  onChange: (next: DraftDecision) => void
  /** sufixo `${ci}-${pi}` dos data-testid */
  testId: string
}) {
  const { t, lang } = useI18n()
  const state = draftAllergenState(product)
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<number[]>(() => draftSelection(product))
  const suggestions = product.ingredient_suggestions ?? []

  const openEditor = () => {
    setSelected(draftSelection(product))
    setOpen(true)
  }
  const confirm = () => {
    onChange({ ...decision, allergens: [...selected] })
    setOpen(false)
  }
  const undo = () => {
    const next = { ...decision }
    delete next.allergens
    onChange(next)
  }
  const summary = draftSelection(product)

  return (
    <div
      className="rounded-lg border border-gray-100 bg-gray-50/60 p-2 space-y-2"
      data-testid={`import-review-allergens-${testId}`}
      role="group"
      aria-labelledby={`import-review-allergens-label-${testId}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span
          id={`import-review-allergens-label-${testId}`}
          className="text-xs font-medium text-gray-600"
        >
          {t('admin.menu.allergens')}
        </span>
        <AllergenBadge
          state={draftAllergenBadgeState(product)}
          testId={`import-review-allergens-state-${testId}`}
          prefix={false}
        />
        {!open && (
          <span
            className="text-xs text-gray-500"
            data-testid={`import-review-allergens-summary-${testId}`}
          >
            {/* lista vazia confirmada: o selo `none` já diz "sem alergénios" */}
            {formatAllergenList(
              summary.filter(isAllergenCode).map((c) => allergenLabel(t, c, lang)),
              lang,
            )}
          </span>
        )}
        <span className="flex-1" />
        {!open && state !== 'confirmed' && (
          <button
            type="button"
            data-testid={`import-review-allergens-open-${testId}`}
            onClick={openEditor}
            className="text-xs text-indigo-600 hover:underline"
          >
            {t('admin.import.review_allergens_open')}
          </button>
        )}
        {!open && state === 'confirmed' && (
          <button
            type="button"
            data-testid={`import-review-allergens-undo-${testId}`}
            onClick={undo}
            className="text-xs text-gray-500 hover:underline"
          >
            ↩ {t('admin.import.review_allergens_undo')}
          </button>
        )}
      </div>

      {open && (
        <div className="space-y-2">
          <AllergenChips
            selected={selected}
            sourceOf={(code) => draftSourceOf(product, code)}
            onToggle={(code) => setSelected((s) => toggleCode(s, code))}
            testIdPrefix={`import-review-allergen-${testId}-`}
          />
          {(product.allergens ?? []).length > 0 ? (
            <AllergenLegend />
          ) : (
            <p className="text-[11px] text-amber-700">
              {t('admin.menu.allergens_unreviewed_hint')}
            </p>
          )}
          <div className="flex justify-end">
            <button
              type="button"
              data-testid={`import-review-allergens-confirm-${testId}`}
              onClick={confirm}
              className="rounded-full bg-indigo-600 text-white text-xs px-3 py-1 hover:bg-indigo-700"
            >
              {t('admin.menu.allergens_confirm')}
            </button>
          </div>
        </div>
      )}

      {suggestions.length > 0 && (
        <div
          className="flex flex-wrap items-center gap-1"
          title={t('admin.menu.ingredient_suggestions_hint')}
        >
          <span className="text-xs text-amber-800">
            ✦ {t('admin.menu.ingredient_suggestions')}:
          </span>
          {suggestions.map((s, i) => {
            const d = ingredientDecision(decision, s.name)
            return (
              <button
                key={s.name}
                type="button"
                data-testid={`import-review-ingredient-sug-${testId}-${i}`}
                data-state={d}
                aria-pressed={d === 'accepted'}
                onClick={() => onChange(cycleIngredient(decision, s.name))}
                className={cn(
                  'rounded-full border px-2 py-0.5 text-xs',
                  d === 'accepted'
                    ? 'border-emerald-400 bg-emerald-50 text-emerald-700'
                    : d === 'rejected'
                      ? 'border-gray-200 bg-white text-gray-400 line-through'
                      : 'border-dashed border-amber-300 bg-white text-amber-800',
                )}
              >
                {d === 'accepted' ? '✓ ' : ''}
                {s.name}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
